/**
 * Returns a per-user localStorage key so that preferences stored by user A
 * are never read by user B on a shared browser session.
 *
 * When userId is unknown (null/undefined) the base key is returned as a
 * fallback so callers never produce an invalid key.
 */
export function userLsKey(base: string, userId: string | null | undefined): string {
  return userId ? `${base}-${userId}` : base;
}

/**
 * Removes every localStorage entry whose key ends with `-{userId}`.
 *
 * Call this on logout and on session expiry so that a subsequent user who
 * opens the same browser session cannot observe the previous user's UI state
 * (sidebar preferences, filter selections, dismissed banners, etc.).
 *
 * Safe to call with an empty / null userId — it is a no-op in that case.
 */
export function clearUserScopedStorage(userId: string | null | undefined): void {
  if (!userId) return;
  const suffix = `-${userId}`;
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.endsWith(suffix)) {
      keysToRemove.push(key);
    }
  }
  for (const key of keysToRemove) {
    localStorage.removeItem(key);
  }
}
